'use client';

import { ChangeEvent } from 'react';
import { addressSchema } from 'constant';
import { FormikValues, useFormik } from 'formik';
import {
  useAddAddressMutation,
  useDeleteAddressMutation,
  useGetAddressesQuery,
  useUpdateAddressMutation
} from 'services/addressesApi';
import { useGetStatesQuery } from 'services/locationsApi';

import { Box, Button, Checkbox, Flex, Stack, useTheme } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { transformToReactSelectOptions } from 'utils';

import {
  AddressBaseInterface,
  AddressDataInterface,
  ScreenAddressEnum,
  ScreenAddressType
} from 'types';

import { Loader, SelectField, Text, TextField } from 'ui';
import { NavigationButtons } from '../ShopPackagesFlow';

const initialValues: FormikValues = {
  addr_nickname: '',
  addr1: '',
  addr2: '',
  city: '',
  state: '',
  postal: '',
  is_primary: false,
  is_primary_bill: false
};

interface AddressFormProps {
  onScreenTypeChange?: (type: ScreenAddressType) => void;
  onGuestSubmit?: (id: number) => void;
  initialData?: AddressDataInterface;
  isShopCart?: boolean;
}

const AddressForm = ({
  onScreenTypeChange,
  onGuestSubmit,
  initialData,
  isShopCart
}: AddressFormProps) => {
  const {
    colors: {
      palette: {
        dashboard: {
          account: {
            addresses: { form }
          }
        }
      }
    }
  } = useTheme();

  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);

  const { data: states = [], isLoading: isStatesLoading } =
    useGetStatesQuery('');
  const {
    data: addresses = [],
    isLoading,
    error: addressesError
  } = useGetAddressesQuery('', {
    refetchOnMountOrArgChange: true,
    skip: !!guestInfo
  });
  const [addAddress, { isLoading: isAdding, error: addError }] =
    useAddAddressMutation();
  const [
    updateAddress,
    { isLoading: isUpdateAddressLoading, error: updateError }
  ] = useUpdateAddressMutation();
  const [deleteAddress, { isLoading: isDeleting, error: deleteError }] =
    useDeleteAddressMutation();

  const errorMsg = addressesError || addError || updateError || deleteError;
  const initialFormValues = initialData
    ? {
        addr_nickname: initialData.addr_nickname || '',
        addr1: initialData.addr1 || '',
        addr2: initialData.addr2 || '',
        city: initialData.city || '',
        state: initialData.state || '',
        postal: initialData.postal || '',
        is_primary: initialData.is_primary || false,
        is_primary_bill: initialData.is_primary_bill || false
      }
    : initialValues;

  const {
    handleSubmit,
    getFieldProps,
    validateField,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    values,
    dirty,
    isValid
  } = useFormik({
    initialValues: initialFormValues,
    validationSchema: addressSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (formData: FormikValues) => {
      const addressData = {
        ...formData,
        onSuccess: () => {
          if (userInfo && onScreenTypeChange) {
            onScreenTypeChange(ScreenAddressEnum.AddressList);
          }
        }
      } as AddressBaseInterface;

      if (guestInfo && onGuestSubmit) {
        const response = await addAddress(addressData);
        if ('data' in response) {
          await onGuestSubmit(response.data.addr_id);
        } else {
          console.error('Failed to add guest address:', response.error);
        }
      } else if (userInfo) {
        if (initialData) {
          await updateAddress({ ...addressData, addr_id: initialData.addr_id });
        } else {
          await addAddress(addressData);
        }
      }
    }
  });

  const isAllowDelete = addresses?.length > 1;
  const formattedStates = transformToReactSelectOptions(states);
  const isSubmitDisabled =
    !dirty ||
    !isValid ||
    isLoading ||
    isAdding ||
    isUpdateAddressLoading ||
    isDeleting;

  const getError = (fieldName: string) => errors[fieldName];

  const handleCancel = () => {
    onScreenTypeChange?.(ScreenAddressEnum.AddressList);
  };

  const resetFieldHandler = (field: string) => () => {
    setFieldValue(field, initialValues[field]);
  };

  const handleDelete = async () => {
    if (initialData) {
      await deleteAddress({
        addressId: initialData.addr_id,
        onSuccess: () => onScreenTypeChange?.(ScreenAddressEnum.AddressList)
      });
    }
  };

  const handleCheckboxChange =
    (fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const checkboxValue = e.target.checked;
      setFieldValue(fieldName, checkboxValue ? 1 : 0);
    };

  const handleSelectChange = async (name: string, value: string | number) => {
    await setFieldValue(name, value);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value }
    } = event;
    setFieldValue(name, value);
  };

  return (
    <Box as='form' onSubmit={handleSubmit}>
      {(isLoading || isStatesLoading || isUpdateAddressLoading) && (
        <Loader fixed bg='blackOpacity.10' />
      )}
      <Stack
        bg={form.background}
        borderRadius='6px'
        px={{ base: '15px', md: '23px' }}
        py='25px'
        w='100%'
        gap='0'
      >
        <Stack w='100%' gap='2px'>
          <TextField
            autoFocus
            {...getFieldProps('addr1')}
            error={getError('addr1')}
            touched={!!touched.addr1}
            onReset={resetFieldHandler('addr1')}
            label='Street Address'
            isFieldValue={!!values.addr1}
            onChange={handleInputChange}
            size='lg'
            fontSize='21px'
            containerStyles={{
              '& label': { fontSize: { base: '14px', md: '15px' } }
            }}
          />
          <TextField
            {...getFieldProps('addr2')}
            error={getError('addr2')}
            touched={!!touched.addr2}
            onReset={resetFieldHandler('addr2')}
            label=''
            isFieldValue={!!values.addr2}
            onChange={handleInputChange}
            size='lg'
            fontSize='21px'
            containerStyles={{
              '& label': { fontSize: { base: '14px', md: '15px' } }
            }}
          />
        </Stack>

        <Flex
          w='100%'
          gap='15px'
          mt='17px'
          flexDirection={{ base: 'column', lg: 'row' }}
        >
          <TextField
            {...getFieldProps('city')}
            label='City'
            error={getError('city')}
            onChange={handleInputChange}
            touched={!!touched.city}
            onReset={resetFieldHandler('city')}
            isFieldValue={!!values.city}
            size='lg'
            fontSize='21px'
            containerStyles={{
              '& label': { fontSize: { base: '14px', md: '15px' } }
            }}
          />

          <Flex w='100%' gap='15px'>
            <SelectField
              name='state'
              setFieldValue={handleSelectChange}
              setFieldTouched={setFieldTouched}
              validateField={validateField}
              value={values.state}
              options={formattedStates}
              error={getError('state')}
              touched={!!touched.state}
              isSearchable
              bordered
              placeholder='Select State'
              label='State'
              width='100%'
              stylesControl={{
                height: '60px',
                fontSize: '21px'
              }}
              stylesOption={{
                fontSize: '21px'
              }}
              stylesMenuList={{
                maxHeight: '250px'
              }}
              formControlProps={{
                sx: {
                  '& label': { fontSize: { base: '14px', md: '15px' } }
                }
              }}
            />
            <TextField
              {...getFieldProps('postal')}
              label='ZIP Code'
              error={getError('postal')}
              onChange={handleInputChange}
              touched={!!touched.postal}
              onReset={resetFieldHandler('postal')}
              isFieldValue={!!values.postal}
              size='lg'
              fontSize='21px'
              width={['40%', '70%']}
              inputStyles={{
                paddingRight: '20px'
              }}
              containerStyles={{
                '& label': { fontSize: { base: '14px', md: '15px' } }
              }}
            />
          </Flex>
        </Flex>

        {!guestInfo && (
          <Stack gap='19px' mt='31px'>
            <Checkbox
              variant='outline'
              colorScheme='primary'
              isChecked={values.is_primary_bill}
              {...getFieldProps('is_primary_bill')}
              onChange={handleCheckboxChange('is_primary_bill')}
            >
              <Text
                fontSize={14}
                textTransform='uppercase'
                color={form.checkboxLabelColor}
                ml='4px'
              >
                Primary billing address
              </Text>
            </Checkbox>
            <Checkbox
              variant='outline'
              colorScheme='primary'
              isChecked={values.is_primary}
              {...getFieldProps('is_primary')}
              onChange={handleCheckboxChange('is_primary')}
            >
              <Text
                fontSize={14}
                textTransform='uppercase'
                color={form.checkboxLabelColor}
                ml='4px'
              >
                Primary mailing address
              </Text>
            </Checkbox>
          </Stack>
        )}
      </Stack>

      {!guestInfo && (
        <Box
          px={{
            base: '15px',
            md: '23px'
          }}
          pt='18px'
          pb='20px'
          bg={form.background}
          borderRadius='6px'
          mt='14px'
        >
          <TextField
            {...getFieldProps('addr_nickname')}
            error={getError('addr_nickname')}
            touched={!!touched.name}
            onReset={resetFieldHandler('addr_nickname')}
            label='Add optional nickname for this address'
            isFieldValue={!!values.addr_nickname}
            onChange={handleInputChange}
            size='lg'
            fontSize='21px'
            containerStyles={{
              '& label': { fontSize: { base: '14px', md: '15px' } }
            }}
          />
        </Box>
      )}

      {errorMsg && (
        <Text w='100%' textAlign='center' color='red.300' mt='20px'>
          {errorMsg as string}
        </Text>
      )}

      {isShopCart ? (
        <NavigationButtons
          onBackHandler={handleCancel}
          onNextHandler={handleSubmit}
          isDisabled={isSubmitDisabled}
          nextLabel={guestInfo ? 'Next' : 'Save'}
        />
      ) : (
        <Stack w='100%' gap={{ base: '30px', md: '40px' }}>
          <Flex
            gap='20px'
            marginX='auto'
            w='100%'
            justifyContent='center'
            alignItems='center'
            mt='50px'
            flexDirection={['column', 'row']}
          >
            <Button
              type='submit'
              colorScheme='primary'
              variant='solid'
              width='148px'
              height='45px'
              isDisabled={isSubmitDisabled}
              fontSize='16px'
            >
              Save
            </Button>

            <Button
              onClick={handleCancel}
              colorScheme='primary'
              variant='outline'
              width='148px'
              height='45px'
              fontSize='16px'
            >
              Cancel
            </Button>
          </Flex>
          {!!initialData &&
            (isAllowDelete ? (
              <Button
                colorScheme='red'
                variant='link'
                textTransform='uppercase'
                fontSize='16px'
                _hover={{ textDecoration: 'none' }}
                onClick={handleDelete}
              >
                Delete this address
              </Button>
            ) : (
              <Text
                textTransform='uppercase'
                fontSize='14px'
                color={form.checkboxLabelColor}
                textAlign='center'
              >
                Need to delete this address? Please first add a replacement one
              </Text>
            ))}
        </Stack>
      )}
    </Box>
  );
};

export default AddressForm;
